import { memo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useSectionNavigation } from '@/lib/manual-navigation'
import type { ManualSection } from '@/lib/manual'

/**
 * Markdown link: in-document anchors scroll the section into view without
 * touching the router, every other link opens in a new, isolated tab.
 */
function MarkdownLink({ href, children }: { href?: string; children?: React.ReactNode }) {
  const navigateToSection = useSectionNavigation()

  if (href?.startsWith('#')) {
    const id = decodeURIComponent(href.slice(1))
    return (
      <a
        href={href}
        className="text-primary hover:underline"
        onClick={(event) => {
          event.preventDefault()
          navigateToSection(id)
        }}
      >
        {children}
      </a>
    )
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
      {children}
    </a>
  )
}

/**
 * Tailwind styling for the manual's Markdown, kept here rather than in a
 * typography plugin so tables and code blocks match the rest of the app.
 */
const MARKDOWN_COMPONENTS: Components = {
  a: MarkdownLink,
  h2: ({ children }) => <h2 className="mt-0 text-2xl font-bold text-gray-900">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-6 text-lg font-semibold text-gray-900">{children}</h3>,
  h4: ({ children }) => <h4 className="mt-4 text-base font-semibold text-gray-900">{children}</h4>,
  p: ({ children }) => <p className="mt-4 leading-relaxed text-gray-700">{children}</p>,
  ul: ({ children }) => <ul className="mt-4 list-disc space-y-2 pl-6 text-gray-700">{children}</ul>,
  ol: ({ children }) => <ol className="mt-4 list-decimal space-y-2 pl-6 text-gray-700">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  hr: () => <hr className="mt-6 border-gray-200" />,
  blockquote: ({ children }) => (
    <blockquote className="mt-4 border-l-4 border-primary/40 bg-primary/5 px-4 py-2 text-gray-700">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => (
    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-900">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-gray-100 px-3 py-2 align-top text-gray-700">{children}</td>
  ),
  code: ({ children }) => (
    <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm text-gray-800">{children}</code>
  ),
  // Fenced blocks own their styling: the nested <code> is stripped back so the
  // block reads on the dark background, whether or not it declares a language.
  pre: ({ children }) => (
    <pre className="mt-4 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm leading-relaxed text-gray-100 [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit">
      {children}
    </pre>
  ),
}

export const ManualArticle = memo(function ManualArticle({ section }: { section: ManualSection }) {
  return (
    <section id={section.id} className="scroll-mt-6 rounded-lg border border-gray-200 bg-white p-6">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {section.markdown}
      </ReactMarkdown>
    </section>
  )
})

